import React, { useEffect, useState } from 'react';
import ShowcaseReel from './ShowcaseReel';
import { sportshipPosts, sportshipPostUrl } from './sportship-instagram-data';
import PhoneStack from './PhoneStack';

const SITE_URL = 'https://www.thesportsship.com';
const IG_URL = 'https://www.instagram.com/thesportship';
const TIKTOK_URL = 'https://www.tiktok.com/@thesportship';

const localImg = (num) => `/sportship-brand/thesportship${num}.webp`;

const FullBleed = ({ children }) => (
  <div style={{
    marginLeft: 'calc(-1 * clamp(20px, 4vw, 40px))',
    marginRight: 'calc(-1 * clamp(20px, 4vw, 40px))',
    width: 'calc(100% + 2 * clamp(20px, 4vw, 40px))'
  }}>
    {children}
  </div>
);

/* ---------------------------------------------------------------- HERO --- */

const HeroButton = ({ href, children, primary }) => {
  const [hover, setHover] = useState(false);

  return (
    <a
      href={href}
      target="_blank"
      rel="noopener noreferrer"
      onMouseEnter={() => setHover(true)}
      onMouseLeave={() => setHover(false)}
      style={{
        background: primary
          ? (hover ? 'var(--text-secondary)' : 'var(--text-primary)')
          : (hover ? 'var(--bg-accent)' : 'var(--bg-secondary)'),
        color: primary ? 'var(--bg-primary)' : 'var(--text-primary)',
        border: primary ? '1px solid transparent' : '1px solid var(--border-color)',
        padding: '13px 26px',
        borderRadius: '10px',
        textDecoration: 'none',
        fontWeight: '600',
        fontSize: '14px',
        transition: 'all 0.2s ease',
        transform: hover ? 'translateY(-2px)' : 'none',
        whiteSpace: 'nowrap'
      }}
    >
      {children}
    </a>
  );
};

const MetaItem = ({ label, value }) => (
  <div>
    <div style={{
      fontSize: '11px',
      textTransform: 'uppercase',
      letterSpacing: '0.14em',
      color: 'var(--text-secondary)',
      marginBottom: '6px'
    }}>
      {label}
    </div>
    <div style={{
      fontSize: '14px',
      fontWeight: '600',
      color: 'var(--text-primary)',
      lineHeight: '1.5'
    }}>
      {value}
    </div>
  </div>
);

const Hero = () => (
  <div style={{ padding: 'clamp(32px, 5vw, 64px) 0 0' }}>
    <div style={{ textAlign: 'center' }}>
      <img
        src="/sportship-logo.webp"
        alt="The Sportship — TSS mark"
        style={{
          width: 'clamp(84px, 11vw, 112px)',
          height: 'clamp(84px, 11vw, 112px)',
          borderRadius: '50%',
          objectFit: 'cover',
          display: 'block',
          margin: '0 auto 24px',
          border: '1px solid var(--border-color)',
          boxShadow: '0 10px 30px rgba(0,0,0,0.35)'
        }}
      />
      <span style={{
        color: 'var(--text-secondary)',
        fontSize: '12px',
        fontWeight: '600',
        textTransform: 'uppercase',
        letterSpacing: '0.18em',
        display: 'block',
        marginBottom: '20px'
      }}>
        Sports Media
      </span>
      <h1 style={{
        fontSize: 'clamp(2.5rem, 6vw, 5rem)',
        fontWeight: '800',
        color: 'var(--text-primary)',
        letterSpacing: '0.04em',
        textTransform: 'uppercase',
        margin: '0 0 24px 0',
        lineHeight: '1.05'
      }}>
        THE SPORTSHIP
      </h1>
      <a
        href={IG_URL}
        target="_blank"
        rel="noopener noreferrer"
        style={{
          display: 'inline-block',
          color: 'var(--text-secondary)',
          fontSize: '14px',
          fontWeight: '600',
          textDecoration: 'none',
          marginBottom: '24px'
        }}
      >
        @thesportship
      </a>
      <p style={{
        fontSize: 'clamp(0.95rem, 1.5vw, 1.15rem)',
        color: 'var(--text-secondary)',
        lineHeight: '1.75',
        maxWidth: '640px',
        margin: '0 auto'
      }}>
        A sports media and data platform: scores, tables, player stats and
        ratings for ten football competitions and the NBA, in English and
        Arabic. It started as a football content brand on Instagram and TikTok.
      </p>

      <div style={{
        display: 'flex',
        gap: '12px',
        flexWrap: 'wrap',
        justifyContent: 'center',
        marginTop: '36px'
      }}>
        <HeroButton href={SITE_URL} primary>Visit thesportsship.com ↗</HeroButton>
        <HeroButton href={IG_URL}>Instagram ↗</HeroButton>
        <HeroButton href={TIKTOK_URL}>TikTok ↗</HeroButton>
      </div>
    </div>

  </div>
);


const Credits = () => (
  <div style={{
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))',
    gap: 'clamp(24px, 4vw, 48px)'
  }}>
    <MetaItem label="Role" value="Sole creator — brand, editorial, motion, and the website" />
    <MetaItem label="Disciplines" value="Identity, editorial, web, social" />
    <MetaItem label="Timeline" value="2025 — ongoing" />
    <MetaItem label="Tools" value="Photoshop, Illustrator, Premiere Pro, custom compositing" />
  </div>
);


/* ------------------------------------------------------ SECTION HEADER --- */

const SectionHeader = ({ index, title, note }) => (
  <div style={{
    borderTop: '1px solid var(--border-color)',
    paddingTop: 'clamp(18px, 2.5vw, 26px)',
    marginBottom: 'clamp(22px, 3vw, 34px)'
  }}>
    <div style={{
      display: 'flex',
      alignItems: 'baseline',
      gap: 'clamp(12px, 2vw, 20px)',
      flexWrap: 'wrap'
    }}>
      <span style={{
        fontSize: '12px',
        fontWeight: '700',
        letterSpacing: '0.16em',
        color: 'var(--text-secondary)',
        fontVariantNumeric: 'tabular-nums'
      }}>
        {index}
      </span>
      <h2 style={{
        margin: 0,
        fontSize: 'clamp(1.35rem, 3vw, 2rem)',
        fontWeight: '700',
        color: 'var(--text-primary)',
        letterSpacing: '-0.01em'
      }}>
        {title}
      </h2>
      {note && (
        <span style={{
          fontSize: '12px',
          color: 'var(--text-secondary)',
          marginLeft: 'auto'
        }}>
          {note}
        </span>
      )}
    </div>
  </div>
);

/* ------------------------------------------------------ INSIDE THE SITE --- */

const SITE_PAGES = [
  { file: 'home', label: 'Home', path: '/' },
  { file: 'plus', label: 'TSS+ ratings', path: '/plus', mark: 'tss' },
  { file: 'pl', label: 'Premier League', path: '/league/pl' },
  { file: 'laliga', label: 'La Liga', path: '/league/laliga' },
  { file: 'haaland', label: 'Player — Haaland', path: '/player/pl/manchester-city/erling-haaland' },
  { file: 'yamal', label: 'Player — Yamal', path: '/player/laliga/barcelona/lamine-yamal' },
  { file: 'arsenal', label: 'Club — Arsenal', path: '/team/pl/arsenal' },
  { file: 'compare', label: 'Compare players', path: '/compare/pl' },
  { file: 'golden-boot', label: 'Golden Boot', path: '/golden-boot' },
  { file: 'nba', label: 'NBA', path: '/nba' },
  { file: 'lebron', label: 'NBA — LeBron', path: '/nba/player/2544' }
];

const COVERAGE = [
  {
    group: 'Football',
    items: ['Premier League', 'La Liga', 'Serie A', 'Bundesliga', 'Ligue 1', 'Saudi League',
      'Liga Portugal', 'Süper Lig', 'MLS', 'Champions League']
  },
  { group: 'Basketball', items: ['NBA: games, standings, teams, players, news'] },
  {
    group: 'Analysis',
    items: ['TSS+ player ratings', 'Player pages and cards', 'Compare players', 'Golden Boot race',
      'Head to head', 'Form table', 'Predictions']
  },
  { group: 'Every day', items: ['Live scores', 'Standings', 'Fixtures', 'Transfers', 'News'] },
  { group: 'Languages', items: ['English', 'Arabic'] }
];

const Coverage = () => (
  <div style={{
    border: '1px solid var(--border-color)', borderRadius: '16px', overflow: 'hidden',
    background: 'var(--bg-secondary)', marginBottom: 'clamp(36px, 5vw, 56px)'
  }}>
    {COVERAGE.map((row, i) => (
      <div key={row.group} style={{
        display: 'flex', flexWrap: 'wrap', gap: '10px 18px', alignItems: 'baseline',
        padding: 'clamp(14px, 2vw, 20px) clamp(16px, 2.4vw, 26px)',
        borderTop: i ? '1px solid var(--border-color)' : 'none'
      }}>
        <div style={{
          width: '120px', flexShrink: 0, fontSize: '11px', fontWeight: 700, letterSpacing: '0.16em',
          textTransform: 'uppercase', color: 'var(--text-secondary)'
        }}>
          {row.group}
        </div>
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: '8px', flex: '1 1 260px' }}>
          {row.items.map((it) => (
            <span key={it} style={{
              padding: '6px 12px', borderRadius: 999, fontSize: '13px', color: 'var(--text-primary)',
              border: '1px solid var(--border-color)', background: 'var(--bg-primary)'
            }}>{it}</span>
          ))}
        </div>
      </div>
    ))}
  </div>
);

/* -------------------------------------------------------- LIVE WEBSITE --- */

const LiveSite = () => {
  const [loaded, setLoaded] = useState(false);

  // A cached iframe can finish loading before React attaches onLoad, so the
  // event never arrives and the placeholder would sit there for good.
  useEffect(() => {
    const t = setTimeout(() => setLoaded(true), 6000);
    return () => clearTimeout(t);
  }, []);

  return (
    <div>
      <div style={{
        borderRadius: '14px',
        overflow: 'hidden',
        border: '1px solid var(--border-color)',
        background: '#000',
        boxShadow: '0 24px 60px rgba(0, 0, 0, 0.28)'
      }}>
        <div style={{
          display: 'flex',
          alignItems: 'center',
          gap: '12px',
          padding: '10px 14px',
          background: 'var(--bg-secondary)',
          borderBottom: '1px solid var(--border-color)'
        }}>
          <div style={{ display: 'flex', gap: '6px', flexShrink: 0 }}>
            {['#ff5f57', '#febc2e', '#28c840'].map((c) => (
              <span key={c} style={{
                width: '11px',
                height: '11px',
                borderRadius: '50%',
                background: c,
                display: 'block'
              }} />
            ))}
          </div>
          <div style={{
            flex: 1,
            background: 'var(--bg-primary)',
            border: '1px solid var(--border-color)',
            borderRadius: '6px',
            padding: '5px 12px',
            fontSize: '12px',
            color: 'var(--text-secondary)',
            textAlign: 'center',
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            whiteSpace: 'nowrap'
          }}>
            thesportsship.com
          </div>
          <a
            href={SITE_URL}
            target="_blank"
            rel="noopener noreferrer"
            style={{
              fontSize: '12px',
              fontWeight: '600',
              color: 'var(--text-primary)',
              textDecoration: 'none',
              flexShrink: 0
            }}
          >
            Open ↗
          </a>
        </div>

        <div style={{
          position: 'relative',
          width: '100%',
          height: 'clamp(460px, 78vh, 820px)',
          background: '#000'
        }}>
          {!loaded && (
            <div style={{
              position: 'absolute',
              inset: 0,
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              justifyContent: 'center',
              gap: '14px',
              zIndex: 0,
              color: 'rgba(255,255,255,0.5)',
              fontSize: '12px',
              letterSpacing: '0.1em',
              textTransform: 'uppercase'
            }}>
              <span style={{
                width: '26px',
                height: '26px',
                border: '2px solid rgba(255,255,255,0.18)',
                borderTopColor: 'rgba(255,255,255,0.7)',
                borderRadius: '50%',
                animation: 'sportshipSpin 0.9s linear infinite'
              }} />
              Loading the live site
            </div>
          )}
          <iframe
            src={`${SITE_URL}/?theme=light&lang=en`}
            title="The Sportship — live website"
            loading="lazy"
            onLoad={() => setLoaded(true)}
            style={{
              position: 'absolute',
              inset: 0,
              width: '100%',
              height: '100%',
              border: 'none',
              display: 'block',
              zIndex: 1
            }}
          />
        </div>
      </div>

      <style>{`@keyframes sportshipSpin { to { transform: rotate(360deg); } }`}</style>

      <p style={{
        fontSize: '13px',
        color: 'var(--text-secondary)',
        textAlign: 'center',
        marginTop: '16px'
      }}>
        That is the real site running live above. If your browser blocks embedded
        frames,{' '}
        <a
          href={SITE_URL}
          target="_blank"
          rel="noopener noreferrer"
          style={{ color: 'var(--text-primary)', fontWeight: '600' }}
        >
          open thesportsship.com in a new tab ↗
        </a>
      </p>
    </div>
  );
};

/* ---------------------------------------------------------------- REELS --- */

const formatCount = (n) => {
  if (n >= 1000000) return `${(n / 1000000).toFixed(n >= 10000000 ? 0 : 1)}M`;
  if (n >= 1000) return `${(n / 1000).toFixed(n >= 10000 ? 0 : 1)}K`;
  return `${n}`;
};

const ReelCard = ({ post, number }) => {
  const [hover, setHover] = useState(false);

  return (
    <a
      href={sportshipPostUrl(post.code)}
      target="_blank"
      rel="noopener noreferrer"
      onMouseEnter={() => setHover(true)}
      onMouseLeave={() => setHover(false)}
      style={{
        position: 'relative',
        display: 'block',
        aspectRatio: '9 / 16',
        borderRadius: '10px',
        overflow: 'hidden',
        background: '#0a0a0a',
        textDecoration: 'none',
        border: '1px solid var(--border-color)',
        transform: hover ? 'translateY(-4px)' : 'none',
        boxShadow: hover ? '0 16px 32px rgba(0,0,0,0.35)' : 'none',
        transition: 'all 0.25s ease'
      }}
    >
      <img
        src={post.file}
        alt={post.caption}
        loading="lazy"
        style={{
          width: '100%',
          height: '100%',
          objectFit: 'cover',
          display: 'block',
          transform: hover ? 'scale(1.05)' : 'scale(1)',
          transition: 'transform 0.5s ease'
        }}
      />

      <div style={{
        position: 'absolute',
        inset: 0,
        background: hover
          ? 'linear-gradient(to top, rgba(0,0,0,0.92) 0%, rgba(0,0,0,0.35) 55%, rgba(0,0,0,0.1) 100%)'
          : 'linear-gradient(to top, rgba(0,0,0,0.75) 0%, transparent 55%)',
        transition: 'background 0.25s ease'
      }} />

      <span style={{
        position: 'absolute',
        top: '8px',
        left: '8px',
        background: 'rgba(0,0,0,0.72)',
        color: 'white',
        fontSize: '11px',
        fontWeight: '700',
        lineHeight: '1',
        padding: '5px 7px',
        borderRadius: '6px',
        fontVariantNumeric: 'tabular-nums'
      }} className="reel-num">
        {number}
      </span>

      {post.isVideo && (
        <span style={{
          position: 'absolute',
          top: '10px',
          right: '10px',
          fontSize: '13px',
          color: 'white',
          opacity: 0.9,
          textShadow: '0 1px 4px rgba(0,0,0,0.6)'
        }}>
          ▶
        </span>
      )}

      <div style={{
        position: 'absolute',
        left: '12px',
        right: '12px',
        bottom: '12px',
        color: 'white'
      }}>
        <div style={{
          display: 'flex',
          alignItems: 'center',
          gap: '10px',
          fontSize: '13px',
          fontWeight: '700',
          marginBottom: hover ? '10px' : '0'
        }} className="reel-meta">
          {post.views ? (
            <span>▶ {formatCount(post.views)}</span>
          ) : null}
          <span>♥ {formatCount(post.likes)}</span>
          <span className="reel-date" style={{ opacity: 0.6, fontWeight: '500' }}>{post.date}</span>
        </div>
        <p style={{
          margin: 0,
          fontSize: '13px',
          lineHeight: '1.5',
          color: 'rgba(255,255,255,0.9)',
          maxHeight: hover ? '120px' : '0',
          overflow: 'hidden',
          opacity: hover ? 1 : 0,
          transition: 'all 0.25s ease'
        }} className="reel-caption">
          {post.caption}
        </p>
      </div>
    </a>
  );
};

// Highest performing first, all the way down
const orderedPosts = [...sportshipPosts].sort((a, b) => {
  const reach = (p) => (p.views ?? 0) || p.likes;
  return reach(b) - reach(a);
});

const ReelGrid = () => {
  const [expanded, setExpanded] = useState(false);
  const visible = expanded ? orderedPosts : orderedPosts.slice(0, 14);

  return (
    <div>
      <style>{`
        .sportship-reels {
          display: grid;
          grid-template-columns: repeat(auto-fill, minmax(150px, 1fr));
          gap: clamp(10px, 1.5vw, 16px);
        }
        @media (max-width: 640px) {
          .sportship-reels {
            grid-template-columns: repeat(3, 1fr);
            gap: 7px;
          }
          .sportship-reels .reel-num { font-size: 9px; padding: 3px 5px; }
          .sportship-reels .reel-meta { font-size: 10px; gap: 5px; white-space: nowrap; }
          .sportship-reels .reel-date { display: none; }
          .sportship-reels .reel-caption { display: none; }
        }
      `}</style>

      <div className="sportship-reels">
        {visible.map((post, i) => (
          <ReelCard key={post.code} post={post} number={i + 1} />
        ))}
      </div>

      {!expanded && orderedPosts.length > 14 && (
        <div style={{ textAlign: 'center', marginTop: '28px' }}>
          <button
            onClick={() => setExpanded(true)}
            style={{
              background: 'var(--bg-secondary)',
              border: '1px solid var(--border-color)',
              color: 'var(--text-primary)',
              padding: '12px 28px',
              borderRadius: '10px',
              fontSize: '14px',
              fontWeight: '600',
              cursor: 'pointer'
            }}
          >
            Show more posts
          </button>
        </div>
      )}
    </div>
  );
};

/* ----------------------------------------------------------- BRAND DECK --- */

const BrandDeck = () => {
  const images = Array.from({ length: 32 }, (_, i) => i + 1);

  return (
    <FullBleed>
      <div style={{ display: 'flex', flexDirection: 'column', gap: '0' }}>
        {images.map((num) => (
          <img
            key={num}
            src={localImg(num)}
            alt={`The Sportship brand system, slide ${num}`}
            loading="lazy"
            style={{
              width: '100%',
              display: 'block',
              borderRadius: '0'
            }}
          />
        ))}
      </div>
    </FullBleed>
  );
};

/* -------------------------------------------------------------- CONTENT --- */

export const theSportshipContent = {
  pageMaxWidth: '1400px',

  breadcrumb: {
    text: 'Back to Home',
    url: '/'
  },

  sections: [
    { id: 'intro-card', blocks: [{ type: 'custom', render: () => <ShowcaseReel slug="the-sportship" card="https://www.thesportsship.com" alt="The Sportship" /> }] },
    {
      id: 'hero',
      spacing: 'clamp(48px, 7vw, 88px)',
      blocks: [{ type: 'custom', render: () => <Hero /> }]
    },
    { id: 'showcase-motion', blocks: [{ type: 'custom', render: () => <ShowcaseReel slug="the-sportship" video alt="The Sportship" /> }] },
    { id: 'showcase-live-site', blocks: [{ type: 'custom', render: () => <ShowcaseReel slug="the-sportship" frames={[1, 2]} alt="The Sportship" /> }] },

    {
      id: 'live-site',
      spacing: 'clamp(64px, 9vw, 120px)',
      blocks: [
        { type: 'custom', render: () => <SectionHeader index="01" title="The Website, Live" note="thesportsship.com" /> },
        { type: 'custom', render: () => <LiveSite /> }
      ]
    },
    { id: 'showcase-inside', blocks: [{ type: 'custom', render: () => <ShowcaseReel slug="the-sportship" frames={[3, 4]} alt="The Sportship" /> }] },

    {
      id: 'inside',
      spacing: 'clamp(64px, 9vw, 120px)',
      blocks: [
        { type: 'custom', render: () => <SectionHeader index="02" title="Inside the Site" note="football · NBA · analysis" /> },
        { type: 'custom', render: () => <Coverage /> },
        { type: 'custom', render: () => <PhoneStack pages={SITE_PAGES} baseUrl={SITE_URL} imgBase="/sportship-site/" /> }
      ]
    },
    { id: 'showcase-how-it-started', blocks: [{ type: 'custom', render: () => <ShowcaseReel slug="the-sportship" frames={[5, 6]} alt="The Sportship" /> }] },

    {
      id: 'how-it-started',
      spacing: 'clamp(64px, 9vw, 120px)',
      blocks: [
        { type: 'custom', render: () => <SectionHeader index="03" title="How It Started" note="@thesportship" /> },
        {
          type: 'text',
          content:
            'The Sportship began as a football content brand: reels on Instagram and TikTok, built on its own identity and editorial series, from Athletes Billionaire Club to the match-day formats. It has since grown into a sports media and data platform. The reels and the original brand system below are where it came from.'
        },
        { type: 'custom', render: () => <ReelGrid /> },
        { type: 'spacer', size: 'medium' },
        { type: 'custom', render: () => <BrandDeck /> }
      ]
    },
    { id: 'showcase-credits', blocks: [{ type: 'custom', render: () => <ShowcaseReel slug="the-sportship" frames={[7, 8]} alt="The Sportship" /> }] },

    {
      id: 'credits',
      spacing: 'clamp(64px, 9vw, 120px)',
      blocks: [
        { type: 'custom', render: () => <SectionHeader index="04" title="Credits" /> },
        { type: 'custom', render: () => <Credits /> }
      ]
    }
  ],

  callToAction: {
    endCard: '/showcase/the-sportship/end.webp',
    brand: { bg: "#0B0B0C", fg: "#FFFFFF", btnBg: "#FFFFFF", btnFg: "#0B0B0C", logo: "/sportship-logo.webp", logoWidth: 72 , titleStyle: "italic", titleWeight: 900 },
    title: 'See The Sportship in motion',
    button: {
      text: 'Visit thesportsship.com',
      url: SITE_URL,
      style: 'sportship'
    }
  }
};
